import { gsap } from 'gsap';

const CARD_OVERLAP_PX = 14;
type StartOrigin = 'center' | 'bottomCenter';

export type LayoutOptions = {
    overlapPx?: number; // default 14
    curvePx?: number; // vertical arc height at edges
    rotateDeg?: number; // total rotation spread across hand
    liftCenter?: number; // lift center card a bit
    animate?: boolean;
    duration?: number; // when animate=true
    ease?: string; // when animate=true
    startOrigin?: StartOrigin;
};

type CardTransform = {
    x: number;
    y: number;
    rotation: number;
    zIndex: number;
};

type HandLayoutResult = {
    containerWidth: number;
    containerHeight: number;
    offsetX: number;
    offsetY: number;
    transforms: CardTransform[];
};

type Point = { x: number; y: number };

function degToRad(degrees: number): number {
    return (degrees * Math.PI) / 180;
}

/**
 * Rotate a point around an origin.
 */
function rotatePointAroundOrigin(point: Point, origin: Point, angleRad: number): Point {
    const deltaX = point.x - origin.x;
    const deltaY = point.y - origin.y;

    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    return {
        x: origin.x + deltaX * cos - deltaY * sin,
        y: origin.y + deltaX * sin + deltaY * cos,
    };
}

/**
 * Predict the axis-aligned bounding box of a rotated rectangle.
 * Rectangle local space is (0..width, 0..height).
 * transformOrigin is provided as normalized (0..1) percentages.
 */
function getRotatedRectBounds(params: {
    width: number;
    height: number;
    translateX: number;
    translateY: number;
    rotationDeg: number;
    originXPercent: number; // 0..1
    originYPercent: number; // 0..1
}): { minX: number; maxX: number; minY: number; maxY: number } {
    const { width, height, translateX, translateY, rotationDeg, originXPercent, originYPercent } = params;

    const originLocal: Point = {
        x: width * originXPercent,
        y: height * originYPercent,
    };

    const cornersLocal: Point[] = [
        { x: 0, y: 0 },
        { x: width, y: 0 },
        { x: width, y: height },
        { x: 0, y: height },
    ];

    const angleRad = degToRad(rotationDeg);

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const cornerLocal of cornersLocal) {
        const rotatedLocal = rotatePointAroundOrigin(cornerLocal, originLocal, angleRad);

        // Then apply translation (x/y from GSAP)
        const worldPoint: Point = {
            x: rotatedLocal.x + translateX,
            y: rotatedLocal.y + translateY,
        };

        minX = Math.min(minX, worldPoint.x);
        maxX = Math.max(maxX, worldPoint.x);
        minY = Math.min(minY, worldPoint.y);
        maxY = Math.max(maxY, worldPoint.y);
    }

    return { minX, maxX, minY, maxY };
}

/**
 * Computes per-card transforms AND predicted container bounds in one pass.
 * Assumes all cards have same size (uses cards[0]).
 * Assumes the container's coordinate space is where you apply x/y.
 */
export function computeHandLayout(cards: HTMLElement[], opts: LayoutOptions = {}): HandLayoutResult {
    const overlapPx = opts.overlapPx ?? CARD_OVERLAP_PX;
    const curvePx = opts.curvePx ?? 28;
    const rotateDeg = opts.rotateDeg ?? 10;
    const liftCenter = opts.liftCenter ?? 0;

    const cardCount = cards.length;
    if (cardCount === 0) {
        return { containerWidth: 0, containerHeight: 0, offsetX: 0, offsetY: 0, transforms: [] };
    }

    const cardWidth = cards[0]!.offsetWidth || 0;
    const cardHeight = cards[0]!.offsetHeight || 0;

    // If the component is mounted while hidden or not yet rendered, offsetWidth/offsetHeight
    // can be zero, which would break the layout math. In that case, log a warning and
    // return a neutral layout so callers still receive a transform per card.
    if (cardWidth === 0 || cardHeight === 0) {
        console.warn(
            '[computeHandLayout] Card dimensions are zero; skipping layout computation. ' +
                'This can happen if the hand is rendered while hidden.',
        );
        const neutralTransforms: CardTransform[] = [];
        for (let index = 0; index < cardCount; index++) {
            neutralTransforms.push({
                x: 0,
                y: 0,
                rotation: 0,
                zIndex: cardCount - index,
            });
        }
        return {
            containerWidth: 0,
            containerHeight: 0,
            offsetX: 0,
            offsetY: 0,
            transforms: neutralTransforms,
        };
    }

    const stepX = Math.max(0, cardWidth - overlapPx);
    const totalWidth = stepX * (cardCount - 1) + cardWidth;

    const denominator = Math.max(1, cardCount - 1);

    const transforms: CardTransform[] = [];

    // Aggregate predicted bounds across all rotated cards
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (let index = 0; index < cardCount; index++) {
        const t = (index / denominator) * 2 - 1; // -1..1

        const baseX = index * stepX - totalWidth / 2 + cardWidth / 2;

        // Your parabola (0 center, up at edges)
        const curveY = curvePx * (t * t);

        const rotation = (rotateDeg / 2) * t;

        const centerLift = liftCenter ? -liftCenter * (1 - Math.abs(t)) : 0;

        const translateX = baseX;
        const translateY = curveY + centerLift;

        const transform: CardTransform = {
            x: translateX,
            y: translateY,
            rotation,
            zIndex: cardCount - index,
        };

        transforms.push(transform);

        // transformOrigin: '50% 100%' => bottom-center pivot
        const rectBounds = getRotatedRectBounds({
            width: cardWidth,
            height: cardHeight,
            translateX,
            translateY,
            rotationDeg: rotation,
            originXPercent: 0.5,
            originYPercent: 1.0,
        });

        minX = Math.min(minX, rectBounds.minX);
        maxX = Math.max(maxX, rectBounds.maxX);
        minY = Math.min(minY, rectBounds.minY);
        maxY = Math.max(maxY, rectBounds.maxY);
    }

    if (!isFinite(minX)) {
        return { containerWidth: 0, containerHeight: 0, offsetX: 0, offsetY: 0, transforms };
    }

    const offsetX = -minX;
    const offsetY = -minY;

    const containerWidth = Math.ceil(maxX - minX);
    const containerHeight = Math.ceil(maxY - minY);

    return { containerWidth, containerHeight, offsetX, offsetY, transforms };
}

export function applyHandLayoutFromCenter(container: HTMLElement, cards: HTMLElement[], opts: LayoutOptions = {}) {
    gsap.killTweensOf(cards);
    const layout = computeHandLayout(cards, opts); // your one-pass math version (no DOM rect needed)
    const duration = opts.duration ?? 0.4;
    const ease = opts.ease ?? 'power2.out';
    const startOrigin: StartOrigin = opts.startOrigin ?? 'center';

    if (cards.length === 0) return;

    // Ensure a stable coordinate space
    gsap.set(container, { position: 'relative' });
    gsap.set(cards, { position: 'absolute', left: 0, top: 0 });

    // Set container size first so center coords are meaningful
    gsap.set(container, { width: layout.containerWidth, height: layout.containerHeight });

    const sampleCard = cards.find((card) => card.offsetWidth > 0 && card.offsetHeight > 0);
    if (!sampleCard) {
        return;
    }
    // Measure card size (assumes same size)
    const cardWidth = sampleCard.offsetWidth;
    const cardHeight = sampleCard.offsetHeight;

    // Compute start pose in the same space as GSAP x/y (top-left)
    const centerX = layout.containerWidth / 2;
    const centerY = layout.containerHeight / 2;

    const startX = centerX - cardWidth / 2;

    const startY = startOrigin === 'center' ? centerY - cardHeight / 2 : layout.containerHeight - cardHeight; // bottomCenter

    const targets = cards.map((cardEl, index) => {
        const t = layout.transforms[index]!;
        return {
            el: cardEl,
            x: t.x + layout.offsetX,
            y: t.y + layout.offsetY,
            rotation: t.rotation,
            zIndex: t.zIndex,
        };
    });

    // Put everything at the center first
    gsap.set(cards, {
        x: startX,
        y: startY,
        rotation: 0,
        transformOrigin: '50% 100%',
    });
    targets.forEach((t) => gsap.set(t.el, { zIndex: t.zIndex }));

    if (!opts.animate) {
        // Snap to final
        targets.forEach((t) => gsap.set(t.el, { x: t.x, y: t.y, rotation: t.rotation }));
        return;
    }

    // Animate to final layout
    gsap.to(
        targets.map((t) => t.el),
        {
            duration,
            ease,
            x: (index) => targets[index]!.x,
            y: (index) => targets[index]!.y,
            rotation: (index) => targets[index]!.rotation,
        },
    );
}

// Force container size calculations by initially laying out the cards with opacity 0
// Then, back to left:0, opacity:1 and usual animation
// This is necessary because if collapsed is true initially, we can't calculate the container size until they are uncollapsed.
export function initializeCards(container: HTMLElement, cards: HTMLElement[], opts: LayoutOptions) {
    gsap.set(cards, {
        opacity: 0,
    });
    applyHandLayoutFromCenter(container, cards, opts);
    gsap.set(cards, {
        left: 0,
        opacity: 1,
    });
}

export function applyStackedLayout(container: HTMLElement, cards: HTMLElement[]) {
    if (!container || cards.length === 0) return;
    gsap.killTweensOf(cards);
    // Use current container size (do NOT change it)
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const cardWidth = cards[0]!.offsetWidth;
    const cardHeight = cards[0]!.offsetHeight;

    // Target center (top-left translation that centers the card)
    const stackCenterX = containerWidth / 2 - cardWidth / 2;
    const stackCenterY = containerHeight / 2 - cardHeight / 2;

    // Make it feel like a "pile" (small jitter + random rotation)
    const maxJitterPx = 4;
    const maxRotationDeg = 10;

    cards.forEach((card) => {
        gsap.to(card, {
            x: stackCenterX + gsap.utils.random(-maxJitterPx, maxJitterPx),
            y: stackCenterY + gsap.utils.random(-maxJitterPx, maxJitterPx),
            rotation: gsap.utils.random(-maxRotationDeg, maxRotationDeg),
            // duration: 0.35,
            duration: 0.35,
            ease: 'power2.out',
        });
    });
}
